import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import CustomLabel from "../CustomLabel";
import ConfirmDialog from "../ConfirmDialog";
import {
  cancelClientPackage,
  getClientPackages,
} from "../../services/PackagesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClientPackage } from "./types";
import { formatStatusLabel } from "./classUtils";
import { useClientOptions } from "./useClassOptions";

interface ClientPackagesDialogProps {
  open: boolean;
  businessId?: string;
  initialClientId?: string;
  onClose: () => void;
}

const statusColor = (status?: string) => {
  switch (String(status || "").toUpperCase()) {
    case "ACTIVE":
      return "#2e7d32";
    case "EXPIRED":
      return "#d32f2f";
    case "CANCELLED":
      return "#666";
    default:
      return "#666";
  }
};

const paymentStatusColor = (status?: string) => {
  switch (String(status || "").toUpperCase()) {
    case "PAID":
      return "#2e7d32";
    case "PARTIAL":
      return "#ed6c02";
    case "UNPAID":
      return "#d32f2f";
    case "REFUNDED":
      return "#1976d2";
    case "CANCELLED":
      return "#666";
    default:
      return "#666";
  }
};

const paymentStatusLabel = (status?: string) => {
  const value = String(status || "").toUpperCase();
  if (value === "UNPAID") return "Awaiting payment";
  if (value === "PARTIAL") return "Partially paid";
  if (!value) return "-";
  return value.charAt(0) + value.slice(1).toLowerCase();
};

const isCancellable = (row: ClientPackage) =>
  String(row.status || "").toUpperCase() !== "CANCELLED";

/** Purchase history for one client — remaining quota, expiry, status and payment. */
const ClientPackagesDialog: React.FC<ClientPackagesDialogProps> = ({
  open,
  businessId,
  initialClientId,
  onClose,
}) => {
  const { options: clientOptions, onSearchInput: onClientSearchInput } =
    useClientOptions(businessId, open, initialClientId);
  const [clientId, setClientId] = React.useState<string>("");
  const [rows, setRows] = React.useState<ClientPackage[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [cancelTarget, setCancelTarget] = React.useState<ClientPackage | null>(
    null
  );
  const [cancelling, setCancelling] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setClientId(initialClientId || "");
      setRows([]);
    }
  }, [open, initialClientId]);

  const fetchPackages = React.useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      setRows(await getClientPackages(clientId));
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load this client's packages."));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  React.useEffect(() => {
    if (!open || !clientId) return;
    fetchPackages();
  }, [clientId, open, fetchPackages]);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelClientPackage(String(cancelTarget.id));
      toast.success("Package cancelled. Any paid amount was refunded.");
      setCancelTarget(null);
      await fetchPackages();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to cancel the package."));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <CustomDialog
        open={open}
        onClose={onClose}
        paperSx={{ minWidth: { sm: 780 }, maxWidth: 960 }}
        title="Client Packages"
        actions={<CustomButton label="Close" variant="outlined" onClick={onClose} />}
      >
        <Box>
          <CustomLabel text="Client" fontSize={14} fontWeight={500} />
          <CustomSelect
            value={clientId}
            options={clientOptions}
            onSearchInput={onClientSearchInput}
            onChange={(v: any) => setClientId(String(v ?? ""))}
            placeholder="Select a client"
            searchable
            fullWidth
          />
        </Box>

        <Box sx={{ mt: 2, overflowX: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !clientId ? (
            <Typography sx={{ color: "#777", fontSize: 14 }}>
              Pick a client to see the packages they bought.
            </Typography>
          ) : rows.length === 0 ? (
            <Typography sx={{ color: "#777", fontSize: 14 }}>
              This client has not purchased any package yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Package</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.package?.name || "-"}</TableCell>
                    <TableCell align="right">
                      {row.sessions_remaining ?? "-"}
                    </TableCell>
                    <TableCell align="right">{row.sessions_total ?? "-"}</TableCell>
                    <TableCell>{formatTableDate(row.expires_at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={formatStatusLabel(row.status)}
                        sx={{
                          bgcolor: `${statusColor(row.status)}1a`,
                          color: statusColor(row.status),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          String(row.payment_status || "").toUpperCase() ===
                          "UNPAID"
                            ? "Not bookable yet — waiting on payment"
                            : ""
                        }
                      >
                        <Chip
                          size="small"
                          label={paymentStatusLabel(row.payment_status)}
                          sx={{
                            bgcolor: `${paymentStatusColor(row.payment_status)}1a`,
                            color: paymentStatusColor(row.payment_status),
                            fontWeight: 600,
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Cancel package">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!isCancellable(row)}
                            onClick={() => setCancelTarget(row)}
                          >
                            <CancelIcon
                              sx={{
                                color: isCancellable(row) ? "#d32f2f" : "#D3D3D3",
                                width: 20,
                              }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </CustomDialog>

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onClose={() => {
          if (!cancelling) setCancelTarget(null);
        }}
        title="Cancel package"
        message={`Cancel "${
          cancelTarget?.package?.name || "this package"
        }"? Any amount already paid is refunded, and the remaining sessions become unusable.`}
        confirmButtonText={cancelling ? "Cancelling..." : "Cancel Package"}
        onConfirm={handleCancel}
      />
    </>
  );
};

export default ClientPackagesDialog;
